import useSWR from "swr";

export interface SupportAccessSession {
	active: boolean;
	grantId?: string;
	subjectUserId?: number;
	/** Absent for an account-wide session — the wider scope, not a missing value. */
	companyId?: number | null;
	origin?: "consent" | "break_glass" | "override";
	expiresAt?: string;
	/**
	 * What the session may DO. Only an override session can be `"write"`.
	 *
	 * 🪤 ABSENT MEANS READ. Treat anything that is not exactly `"write"` as
	 * read-only: an older API pod omits the field entirely, and a UI that reads
	 * `mode !== "read"` would show the alarming write-mode bar to every operator
	 * served by that pod.
	 */
	mode?: "read" | "write";
}

export const SUPPORT_ACCESS_SESSION_KEY = "support-access/session";

/**
 * The SERVER's account of the session this tab is in.
 *
 * 🔑 Everything the interface says about a support session must come from here,
 * never from what the client remembered. During a mixed-version deploy an old
 * pod ignores the act-as header entirely and serves the request as the staff
 * member — a client-remembered banner would then claim a customer session that
 * is not in effect, which is worse than no banner at all, because the banner IS
 * the safety mechanism.
 *
 * Revalidated on focus and on an interval, so a session ended elsewhere (by the
 * customer, by expiry, by another staff member) stops being claimed here within
 * seconds rather than at the next navigation.
 */
export function useSupportAccessSession() {
	const { data, error, isLoading, mutate } = useSWR<SupportAccessSession>(
		SUPPORT_ACCESS_SESSION_KEY,
		{
			revalidateOnFocus: true,
			refreshInterval: 30_000,
			// A failed echo must not be rendered as "no session" — see
			// `isUnknown` below. Keeping the last value avoids flapping the bar
			// off and on across a transient network error.
			keepPreviousData: true,
		},
	);

	return {
		session: data,
		/**
		 * The server could not be asked. NOT the same as "no session": absence of
		 * an answer must never render as absence of a session.
		 */
		isUnknown: Boolean(error) || (isLoading && !data),
		refresh: mutate,
	};
}
